// ext-ui/hooks/filters/fieldHelpers.ts
import moment, { Moment } from 'moment'
import queryString from 'query-string'

import { FieldCodec } from './schemaBuilder'

// string → UI: string | undefined
export const stringField = <T extends string>(def?: T): FieldCodec<T | undefined, string | undefined> => ({
  default: def,
  fromQuery: (raw) => (typeof raw === 'string' ? (raw as T) : def),
  toQuery: (val) => (val ? val : undefined),
  toApi: (val) => (val ? val : undefined),
  equals: (a, b) => (a ?? '').trim() === (b ?? '').trim(),
})

// number → UI: number | undefined
export const numberField = <T extends number>(def?: T): FieldCodec<T | undefined, number | undefined> => ({
  default: def,
  fromQuery: (raw) => (raw !== undefined && !isNaN(Number(raw)) ? (Number(raw) as T) : def),
  toQuery: (val) => (val !== undefined ? String(val) : undefined),
  toApi: (val) => (val !== undefined && !Number.isNaN(val) ? val : undefined),
  equals: (a, b) => (a === undefined ? undefined : a) === (b === undefined ? undefined : b),
})

// boolean → UI: boolean | undefined
export const booleanField = (def?: boolean): FieldCodec<boolean | undefined, boolean | undefined> => ({
  default: def,
  fromQuery: (raw) => (raw !== undefined ? raw === 'true' : def),
  toQuery: (val) => (val !== undefined ? String(val) : undefined),
  toApi: (val) => (val !== undefined ? val : undefined),
  equals: (a, b) => a === b, // preserves undefined vs false distinction
})

// enum → UI: E | undefined
export const enumField = <E extends string>(def?: E): FieldCodec<E | undefined, E | undefined> => ({
  default: def,
  fromQuery: (raw) => (typeof raw === 'string' ? (raw as E) : def),
  toQuery: (val) => (val !== undefined && val !== def ? val : undefined),
  toApi: (val) => (val !== undefined && val !== def ? val : undefined),
  equals: (a, b) => a === b,
})

const isEmptySet = (s?: Set<string>) => !s || s.size === 0

// Set<string> → UI: Set<string> | undefined
export const stringSetField = (
  def?: Set<string>
): FieldCodec<Set<string> | undefined, string[] | undefined> => ({
  default: def,
  fromQuery: (raw) => {
    if (raw === undefined) return def
    const arr = Array.isArray(raw) ? raw : [raw]
    return new Set(arr)
  },
  toQuery: (val) => (val && val.size > 0 ? Array.from(val) : undefined),
  toApi:   (val) => (val && val.size > 0 ? Array.from(val) : undefined),
  // IMPORTANT: undefined ≡ empty Set
  equals: (a, b) => {
    if (isEmptySet(a) && isEmptySet(b)) return true
    if (!a || !b) return false
    if (a.size !== b.size) return false
    for (const v of a) if (!b.has(v)) return false
    return true
  },
})


// Moment date → UI: Moment | undefined
export const dateField = (def?: Moment): FieldCodec<Moment | undefined, string | undefined> => ({
  default: def,
  fromQuery: (raw) => (typeof raw === 'string' ? moment(raw) : def),
  toQuery: (val, { defaults }) => {
    if (!val) return undefined
    if (defaults && val.isSame(defaults, 'day')) return undefined
    return val.format('YYYY-MM-DD')
  },
  toApi: (val) => (val ? val.format('YYYY-MM-DD') : undefined),
  equals: (a, b) => {
    if (!a && !b) return true
    if (!a || !b) return false
    return a.isSame(b, 'day')
  },
})

// Record<string, Set<string>> → UI: Record<...> | undefined
export const recordOfStringSetsField = (
  def?: Record<string, Set<string>>
): FieldCodec<Record<string, Set<string>> | undefined, string | string[] | undefined> => ({
  default: def,
  fromQuery: (raw) => {
    if (typeof raw !== 'string') return def
    const parsed = queryString.parse(raw) as Record<string, string | string[]>
    const out: Record<string, Set<string>> = {}
    for (const [k, v] of Object.entries(parsed)) {
      const arr = Array.isArray(v) ? v : v ? [v] : []
      if (arr.length) out[k] = new Set(arr)
    }
    return out
  },
  toQuery: (val) => {
    if (!val) return undefined
    const obj: Record<string, string[]> = {}
    for (const [k, set] of Object.entries(val)) {
      if (set && set.size) obj[k] = Array.from(set)
    }
    return Object.keys(obj).length ? queryString.stringify(obj) : undefined
  },
  toApi: () => undefined,
  // IMPORTANT: {}, undefined, or only-empty-sets ≡ equal
  equals: (a, b) => {
    const normalize = (r?: Record<string, Set<string>>) => {
      const m = new Map<string, Set<string>>()
      if (!r) return m
      for (const [k, s] of Object.entries(r)) if (s && s.size) m.set(k, s)
      return m
    }
    const A = normalize(a), B = normalize(b)
    if (A.size !== B.size) return false
    for (const [k, setA] of A) {
      const setB = B.get(k)
      if (!setB) return false
      if (setA.size !== setB.size) return false
      for (const v of setA) if (!setB.has(v)) return false
    }
    return true
  },
})
